import React from "react"
import { useNavigate } from "react-router-dom"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "src/components/ui/table"
import { Badge } from "src/components/ui/badge"
import { formatMoney, formatDate, DEAL_STATUS_BADGE, DEAL_STATUS_LABEL } from "../constants"
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react"

export function DealsTable({ deals, loading }) {
  const navigate = useNavigate()

  const [sortField, setSortField] = React.useState(null)
  const [sortDirection, setSortDirection] = React.useState(null)

  const handleSort = (field) => {
    if (sortField === field) {
      if (sortDirection === "asc") setSortDirection("desc")
      else if (sortDirection === "desc") {
        setSortField(null)
        setSortDirection(null)
      }
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
  }

  const sortedDeals = React.useMemo(() => {
    if (!sortField || !sortDirection) return deals
    return [...deals].sort((a, b) => {
      let aVal, bVal
      if (sortField === "deal") {
        aVal = a.name || `Deal #${a.id}`
        bVal = b.name || `Deal #${b.id}`
      } else if (sortField === "lead") {
        aVal = a.lead?.name || ""
        bVal = b.lead?.name || ""
      } else if (sortField === "stage") {
        aVal = a.stage?.name || ""
        bVal = b.stage?.name || ""
      } else if (sortField === "status") {
        aVal = a.status || ""
        bVal = b.status || ""
      } else if (sortField === "value") {
        aVal = Number(a.value || 0)
        bVal = Number(b.value || 0)
      } else if (sortField === "owner") {
        aVal = a.owner?.full_name || ""
        bVal = b.owner?.full_name || ""
      } else if (sortField === "expected_close") {
        aVal = a.expected_close_on || ""
        bVal = b.expected_close_on || ""
      }

      if (typeof aVal === "string") {
        return sortDirection === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal)
      } else {
        if (aVal < bVal) return sortDirection === "asc" ? -1 : 1
        if (aVal > bVal) return sortDirection === "asc" ? 1 : -1
        return 0
      }
    })
  }, [deals, sortField, sortDirection])

  const renderSortableHeader = (label, field, isRight = false) => {
    const isSorted = sortField === field
    return (
      <button
        onClick={() => handleSort(field)}
        className={`inline-flex items-center gap-1 hover:text-black focus:outline-none font-semibold text-black ${isRight ? "ml-auto justify-end" : ""}`}
      >
        <span>{label}</span>
        {isSorted ? (
          sortDirection === "asc" ? (
            <ArrowUp className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5 shrink-0" />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 text-slate-400 opacity-60 hover:opacity-100 shrink-0" />
        )}
      </button>
    )
  }

  if (!loading && deals.length === 0) {
    return (
      <div className="flex h-48 flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 text-sm text-slate-400">
        No deals match these filters.
      </div>
    )
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50px]">#</TableHead>
            <TableHead>{renderSortableHeader("Deal", "deal")}</TableHead>
            <TableHead>{renderSortableHeader("Lead", "lead")}</TableHead>
            <TableHead>{renderSortableHeader("Stage", "stage")}</TableHead>
            <TableHead>{renderSortableHeader("Status", "status")}</TableHead>
            <TableHead className="text-right">
              <div className="flex justify-end">
                {renderSortableHeader("Value", "value", true)}
              </div>
            </TableHead>
            <TableHead>{renderSortableHeader("Owner", "owner")}</TableHead>
            <TableHead>{renderSortableHeader("Expected close", "expected_close")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedDeals.map((deal, idx) => (
            <TableRow
              key={deal.id}
              className="cursor-pointer"
              onClick={() => navigate(`/deals/${deal.id}`)}
            >
              <TableCell className="text-xs text-slate-400">{idx + 1}</TableCell>
              <TableCell className="font-medium text-slate-800">{deal.name || `Deal #${deal.id}`}</TableCell>
              <TableCell className="text-slate-600">{deal.lead?.name || "—"}</TableCell>
              <TableCell className="text-slate-600">{deal.stage?.name || "—"}</TableCell>
              <TableCell>
                <Badge variant={DEAL_STATUS_BADGE[deal.status] || "secondary"}>
                  {DEAL_STATUS_LABEL[deal.status] || deal.status}
                </Badge>
              </TableCell>
              <TableCell className="text-right font-medium text-slate-700">{formatMoney(deal.value)}</TableCell>
              <TableCell className="text-slate-600">{deal.owner?.full_name || "—"}</TableCell>
              <TableCell className="text-slate-500">{formatDate(deal.expected_close_on)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
